import { useState } from "react";
import {
  getDefaultReportUpdateConfig,
  type ReportUpdateConfigValue,
} from "../lib/utils/reportUpdateConfig";
import { ReportUpdateConfigFields } from "./ReportUpdateConfigFields";
import styles from "./ReportForm.module.scss";

export type ReportFormValues = {
  name: string;
} & ReportUpdateConfigValue;

interface ReportFormProps {
  onSubmit: (values: ReportFormValues) => void;
  isSubmitting: boolean;
  onCancel?: () => void;
}

export function ReportForm({
  onSubmit,
  isSubmitting,
  onCancel,
}: ReportFormProps) {
  const [reportName, setReportName] = useState("");
  const [schedule, setSchedule] = useState(getDefaultReportUpdateConfig);

  const isSubmitDisabled =
    !reportName.trim() || !schedule.updateTillDate || isSubmitting;

  const handleSubmit = () => {
    if (isSubmitDisabled) return;

    onSubmit({
      name: reportName.trim(),
      updateTillDate: schedule.updateTillDate,
      updateForIntervalHourFrequency: schedule.updateForIntervalHourFrequency,
    });
    setReportName("");
    setSchedule(getDefaultReportUpdateConfig());
  };

  const handleCancel = () => {
    setReportName("");
    setSchedule(getDefaultReportUpdateConfig());
    onCancel?.();
  };

  return (
    <div className={styles.reportForm}>
      <div className={styles.formBody}>
        <div className={styles.formField}>
          <label className={styles.fieldLabel} htmlFor="report-name">
            Report Name
          </label>
          <input
            id="report-name"
            type="text"
            placeholder="e.g. Q4 Competitor Analysis"
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            className={styles.nameInput}
            autoFocus
          />
          <p className={styles.helperText}>
            This will create an empty custom report. You can add content from
            any campaign or creator later.
          </p>
        </div>

        <div className={styles.scheduleSection}>
          <p className={styles.scheduleTitle}>Update schedule</p>
          <ReportUpdateConfigFields value={schedule} onChange={setSchedule} />
        </div>
      </div>

      <div className={styles.formFooter}>
        {onCancel && (
          <button
            onClick={handleCancel}
            className={styles.cancelButton}
            type="button"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          className={styles.createButton}
          type="button"
        >
          {isSubmitting ? "Creating..." : "Create Report"}
        </button>
      </div>
    </div>
  );
}
